import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { DirectConnectMilestoneReport, MilestoneStatusResponses } from '../../shared/models';
import { DateUtils } from '../../shared/utils';
import { FormatCurrency } from '../../shared/utils/number';
import { CerebroDrawer, CerebroHeading, CerebroText } from '../../shared/components';

interface DirectConnectMilestoneDetailsProps {
	report: DirectConnectMilestoneReport | null | undefined;
	close: () => void;
}

export const DirectConnectMilestoneDetails: React.FC<DirectConnectMilestoneDetailsProps> = ({ report, close }) => {
	const getFundedText = (report: DirectConnectMilestoneReport): JSX.Element => {
		const fundAmount = report.amountFunded != null ? FormatCurrency(report.amountFunded) : '';
		const fundDate =
			report.dateFunded != null ? DateUtils.FormatStringAsShortDate(report.dateFunded.toString()) : '';

		return (
			<CerebroText>
				{fundAmount} funded on {fundDate}
			</CerebroText>
		);
	};

	return (
		<CerebroDrawer open={report != null} onClose={close}>
			<CerebroHeading size="h1" gutterBottom>
				Lender Progress Report Details
			</CerebroHeading>
			<CerebroHeading size="h3" gutterBottom>
				{report?.lenderName} ({report?.bankName})
			</CerebroHeading>

			<TableContainer sx={{ marginTop: 3 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Stage</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Details</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!report?.activeUser && (
							<TableRow>
								<TableCell colSpan={3} sx={{ textAlign: 'center' }}>
									Inactive Lender
								</TableCell>
							</TableRow>
						)}
						{!report?.activeUser && report?.hasRoomOptOutReasons && (
							<TableRow>
								<TableCell>Opt-Out Reasons</TableCell>
								<TableCell colSpan={2}>
									{report.roomDeclineReasonList.map(reason => (
										<CerebroText key={'decline-' + reason}>{reason}</CerebroText>
									))}
									{report.otherRoomDeclineReason != '' && (
										<CerebroText>{report.otherRoomDeclineReason}</CerebroText>
									)}
									{report.roomReasonDetail != '' && <CerebroText>{report.roomReasonDetail}</CerebroText>}
								</TableCell>
							</TableRow>
						)}
						<TableRow>
							<TableCell>Documents Reviewed</TableCell>
							<TableCell>{getResponseText(report?.documentsDownloaded)}</TableCell>
							<TableCell>
								{report?.documentsDownloadedDate != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.documentsDownloadedDate.toString())}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Term Sheet Submitted</TableCell>
							<TableCell>{getResponseText(report?.termSheetSubmitted)}</TableCell>
							<TableCell>
								{report?.termSheetSubmittedOn != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.termSheetSubmittedOn.toString())}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Underwriting</TableCell>
							<TableCell>{getResponseText(report?.underwriting)}</TableCell>
							<TableCell>
								{report?.underwritingOn != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.underwritingOn.toString())}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Funded</TableCell>
							<TableCell>{getResponseText(report?.funded)}</TableCell>
							<TableCell>{report?.funded == 'Yes' && getFundedText(report)}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</CerebroDrawer>
	);
};

function getResponseText(response?: MilestoneStatusResponses) {
	if (response == 'Yes') {
		return 'Complete';
	}
	if (response == 'OptOut') {
		return 'Opt-Out';
	}

	return 'Pending';
}
